import { isIntegrationId } from 'integrations/common/streams/activeIntegrations/utils';
import {
  UNIFIED_INTEGRATION_IDS,
  UNIFIED_INTEGRATION_IDS_WITH_PROGRESS,
} from './constants';
import {
  AuthMethodFields,
  UnifiedIntegrationId,
  UnifiedIntegrationParams,
  UnifiedIntegrationWithAuth,
} from './types';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { UnifiedAuthMethod } from '@ardoq/api-types/integrations';

export const isUnifiedIntegrationId = (
  id: string | null
): id is UnifiedIntegrationId =>
  isIntegrationId(id) &&
  UNIFIED_INTEGRATION_IDS.includes(id as UnifiedIntegrationId);

export function isUnifiedIntegrationPayload<
  T extends {
    integrationId: IntegrationId;
  },
>(payload: T): payload is T & { integrationId: UnifiedIntegrationId } {
  return isUnifiedIntegrationId(payload.integrationId);
}

export const isImportBuilderIntegrationId = (
  id: string | null
): id is 'import-builder' => id === 'import-builder';

export function isImportBuilderIntegrationPayload<
  T extends { integrationId: IntegrationId },
>(payload: T): payload is T & { integrationId: 'import-builder' } {
  return isImportBuilderIntegrationId(payload.integrationId);
}

export const createUnifiedIntegrationRecord = <T>(
  callbackFn: (id: UnifiedIntegrationId) => T
) => {
  return Object.fromEntries(
    UNIFIED_INTEGRATION_IDS.map(id => [
      id,
      callbackFn(id as UnifiedIntegrationId),
    ])
  ) as Record<UnifiedIntegrationId, T>;
};

export const isIntegrationWithProgress = (
  integrationId?: IntegrationId
): boolean =>
  !!integrationId &&
  isUnifiedIntegrationId(integrationId) &&
  UNIFIED_INTEGRATION_IDS_WITH_PROGRESS.includes(integrationId);

export const isIntegrationWithAuth = (
  params: UnifiedIntegrationParams
): params is UnifiedIntegrationWithAuth => {
  return 'defaultAuthMethod' in params && 'authMethodFields' in params;
};

export const getAuthFields = (integrationState: UnifiedIntegrationParams) => {
  if (isIntegrationWithAuth(integrationState)) {
    return {
      defaultAuthMethod: integrationState.defaultAuthMethod,
      authMethodFields: integrationState.authMethodFields,
    };
  }

  return {
    defaultAuthMethod: 'basic' as UnifiedAuthMethod,
    authMethodFields: {} as AuthMethodFields,
  };
};
